import { LegacyPortalPurchaseHistoryService } from "@spwn-portal/grpc";
import { useSelector } from "store";
import useSWRInfinite from "swr/infinite";
import { useConnectClient } from "utility/connectWeb";

import type { PurchaseHistoryPeriod } from "./purchaseHistoryPeriod";
import type {
  LegacyPortalPurchaseHistoryAvailableYear,
  LegacyPortalPurchaseHistoryOrder,
  LegacyPortalPurchaseHistoryServiceListPurchaseHistoryResponse,
} from "@spwn-portal/grpc";

type Input = {
  /** falseの間は取得を始めない。取得元の切り替え中に二重取得しないため */
  enabled: boolean;
  pageSize: number;
  period: PurchaseHistoryPeriod;
};

type Output = {
  /** 購入履歴がある年と件数（絞り込み前の全期間） */
  availableYears: LegacyPortalPurchaseHistoryAvailableYear[];
  error: unknown;
  hasNextPage: boolean;
  /** 表示できる注文が1件も無いまま取得中かどうか。期間の切り替え中は前の結果を保持するのでfalse */
  isLoading: boolean;
  loadMore: () => void;
  /** 取得済みの全ページを連結した注文。APIの返却順（注文日時の降順）のまま */
  orders: LegacyPortalPurchaseHistoryOrder[];
};

type PageKey = {
  createdAtFrom: number | null;
  createdAtTo: number | null;
  cursor: string;
  uid: string;
};

type Page = {
  /** このページを取得した期間。前の期間の結果を保持している間に、それを今の期間の結果と見分けるため */
  period: PurchaseHistoryPeriod;
  response: LegacyPortalPurchaseHistoryServiceListPurchaseHistoryResponse;
  /** このページを取得したユーザー。前のユーザーの結果を保持している間に、それを今のユーザーの結果として出さないため */
  uid: string;
};

/** 年別件数の判定に使うUTCからのオフセット（分）。JSTなら+540 */
const getUtcOffsetMinutes = (): number => -new Date().getTimezoneOffset();

const isSamePeriod = (
  a: PurchaseHistoryPeriod,
  b: PurchaseHistoryPeriod
): boolean =>
  a.createdAtFrom === b.createdAtFrom && a.createdAtTo === b.createdAtTo;

/**
 * 購入履歴一覧をカーソルでページングしながら取得する。
 * 期間はページをまたいで同じ値を送る必要があるため、呼び出し側で固定した値を受け取る
 */
export const useListPurchaseHistory = (input: Input): Output => {
  const { enabled, pageSize, period } = input;
  const uid = useSelector((state) => state.auth.user.uid);
  const client = useConnectClient(LegacyPortalPurchaseHistoryService);

  const { data, error, isLoading, setSize, size } = useSWRInfinite<
    Page,
    unknown
  >(
    (pageIndex: number, previousPageData: Page | null): PageKey | null => {
      if (!enabled || uid === "") return null;
      // 前ページのカーソルが空なら次ページはない
      if (
        previousPageData !== null &&
        previousPageData.response.nextCursor === ""
      )
        return null;
      return {
        createdAtFrom: period.createdAtFrom,
        createdAtTo: period.createdAtTo,
        cursor:
          pageIndex === 0 ? "" : (previousPageData?.response.nextCursor ?? ""),
        uid,
      };
    },
    async (key: PageKey): Promise<Page> => ({
      period: {
        createdAtFrom: key.createdAtFrom,
        createdAtTo: key.createdAtTo,
      },
      response: await client.listPurchaseHistory({
        ...(key.createdAtFrom === null
          ? {}
          : { createdAtFrom: BigInt(key.createdAtFrom) }),
        ...(key.createdAtTo === null
          ? {}
          : { createdAtTo: BigInt(key.createdAtTo) }),
        ...(key.cursor === "" ? {} : { cursor: key.cursor }),
        pageSize,
        utcOffsetMinutes: getUtcOffsetMinutes(),
      }),
      uid: key.uid,
    }),
    // 期間を切り替えたとき、新しい結果が届くまで前の一覧を残す。画面全体がローディングに戻るのを避ける
    { keepPreviousData: true, revalidateFirstPage: false }
  );

  const fetchedPages = data ?? [];
  const [firstPage] = fetchedPages;
  // ユーザーが切り替わったとき（ログアウトを含む）は、新しい結果を待たずに前のユーザーの一覧を捨てる。
  // 保持したままだと、別のユーザーの注文が今のユーザーの購入履歴として出てしまう
  const isOtherUserData = firstPage !== undefined && firstPage.uid !== uid;
  // 期間を切り替えた直後の取得に失敗したときは前の期間の一覧を捨てる。
  // 保持したままだと、選択中の期間の結果として別の期間の注文が出てしまう
  const isPreviousPeriodData =
    firstPage !== undefined && !isSamePeriod(firstPage.period, period);
  const pages =
    isOtherUserData || (error !== undefined && isPreviousPeriodData)
      ? []
      : fetchedPages.map((page) => page.response);
  const lastPage = pages[pages.length - 1];

  return {
    availableYears: pages[0]?.availableYears ?? [],
    error,
    // 前の期間の一覧を出している間に「もっと見る」を押すと、新しい期間の2ページ目を取りにいってしまう
    hasNextPage:
      !isOtherUserData &&
      !isPreviousPeriodData &&
      lastPage !== undefined &&
      lastPage.nextCursor !== "",
    isLoading: isLoading && pages.length === 0,
    loadMore: () => {
      void setSize(size + 1);
    },
    orders: pages.flatMap((page) => page.orders),
  };
};
